import { InputAdornment, OutlinedInput, Stack, Button, Link, Typography, CircularProgress, Chip } from "@mui/material";
import React, { useEffect, useState } from "react";
import ListPageHeader, {
  ListPageHeaderLeft,
  ListPageHeaderRight,
} from "../../../../../../Components/Misc/ListPageHeader";
import { Link as RouterLink, useNavigate, useOutletContext, useParams } from "react-router";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { routePaths } from "../../../../../../Router/router";
import { ModalContext } from "../../../../../../Components/Modals";
import { ingestService } from "../../../../../../Core/Api/services";
import GlobalConst from "../../../../../../Core/Utils/Constants/globals";
import DataTable, { CellSize } from "../../../../../../Components/Table/table";
import { getGridSingleSelectOperators, GridActionsCellItem, GridColDef, GridRowParams } from "@mui/x-data-grid-premium";
import TableCellStringExpend from "../../../../../../Components/Table/TableCellStringExpend/table-cell-string-expend";
import { GetEnumValueLabelMap } from "../../../../../../Utils/helpers";
import { ObjectType } from "../../../../../../Core/Api/Types/object-type";
import AssetTypeChip from "../../../../../../Components/Misc/Chips/AssetChip/asset-type-chip";
import TableCellBool from "../../../../../../Components/Table/TableCellBool";
import { DbIngestConfigPackInfos } from "../../../../../../Core/Api/Types/db-ingest-config-pack-infos";
import { DbIngestConfigPackPlaceholderRowInfos } from "../../../../../../Core/Api/Types/db-ingest-config-pack-placeholder-row-infos";
import ModalCreateOrUpdatePlaceholder, {
  IngestConfigurePlaceholderContext,
  ModalCreatePlaceholderProps,
} from "../../../../../../Components/Modals/ModalCreateOrUpdatePlaceholder/modal-ingest-config-placeholder-create-or-update";
import ModalExportPlaceholderConfig, {
  ModalExportPlaceholderConfigProps,
} from "../../../../../../Components/Modals/ModalExportPlaceholderConfiguration/modal-ingest-config-placeholder-export-csv";
import { useTranslation } from "react-i18next";
import { ObjectClass } from "../../../../../../Core/Api/Types/object-class";
import AssetClassChip from "../../../../../../Components/Misc/Chips/AssetChip/asset-class-chip";
import TableUuids from "../../../../../../Components/Table/table-uuids";

function IngestPacksSingle() {
  const { t } = useTranslation();
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const [pack, setPack] = useState<DbIngestConfigPackInfos>();
  const [placeholders, setPlaceholders] = useState<DbIngestConfigPackPlaceholderRowInfos[]>([]);

  const [isLoading, setLoading] = useState(true);
  const params = useParams();
  const navigate = useNavigate();

  // Outlet context used to pass props to parent DashboardLayout through react router dom
  const { selectedItemIds, setSelectedItems, reload } = useOutletContext<{
    setSelectedItems: React.Dispatch<React.SetStateAction<number[]>>;
    selectedItemIds: number[];
    reload: number;
  }>();

  const onClickOpenModalPlaceholder = (
    context: IngestConfigurePlaceholderContext,
    placeholder: DbIngestConfigPackPlaceholderRowInfos | undefined = undefined
  ) => {
    if (!openModal) return;

    openModal<ModalCreatePlaceholderProps>(ModalCreateOrUpdatePlaceholder, {
      inputPack: pack,
      inputPlaceholder: placeholder,
      context: context,
      closeModal: () => false,
    });
  };

  const columnsAction: GridColDef<DbIngestConfigPackPlaceholderRowInfos>[] = [
    {
      field: "id",
      headerName: "Id",
      flex: CellSize.small,
      minWidth: CellSize.small,
    },
    {
      field: "name",
      headerName: t("common:name"),
      flex: CellSize.xlarge,
      minWidth: CellSize.xlarge,
      renderCell: (data) => <TableCellStringExpend data={data} />,
    },
    {
      field: "description",
      headerName: t("common:description"),
      flex: CellSize.xlarge,
      minWidth: CellSize.xlarge,
      renderCell: (data) => <TableCellStringExpend data={data} />,
    },
    {
      field: "objectType",
      headerName: t("common:objectType"),
      type: "singleSelect",
      flex: CellSize.medium,
      minWidth: CellSize.medium,
      valueOptions: GetEnumValueLabelMap(ObjectType),
      renderCell: (data) => <AssetTypeChip type={data.value} />,
    },
    {
      field: "objectClasses",
      headerName: t("field:label.objectClasses"),
      type: "singleSelect",
      flex: CellSize.medium,
      minWidth: CellSize.medium,
      valueOptions: GetEnumValueLabelMap(ObjectClass),
      valueGetter: (data, row) => (row.objectClasses ? row.objectClasses : null),
      renderCell: (data) => {
        return data.value ? (
          data.value.map((classification, i) => <AssetClassChip key={i} classification={classification} />)
        ) : (
          <></>
        );
      },
    },
    {
      field: "isOptionnal",
      headerName: t("table:headerName.optional"),
      type: "boolean",
      flex: CellSize.xsmall,
      minWidth: CellSize.xsmall,
      renderCell: (data) => <TableCellBool value={data.value} />,
    },
    {
      field: "actions",
      headerName: "",
      type: "actions",
      flex: CellSize.actionsLarge,
      minWidth: CellSize.actionsLarge,
      align: "right",
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          icon={<FileDownloadOutlinedIcon sx={{ width: 20, height: 20, color: "text.secondary" }} />}
          label={t("table:cellTitle.exportCsv")}
          title={t("table:cellTitle.exportCsv")}
          onClick={() => {
            if (!openModal) return;

            openModal<ModalExportPlaceholderConfigProps>(ModalExportPlaceholderConfig, {
              id: params?.row?.id,
              name: params?.row?.name,
              closeModal: () => false,
            });
          }}
        />,
        <GridActionsCellItem
          icon={<ContentCopyIcon sx={{ width: 20, height: 20, color: "text.secondary" }} />}
          label={t("common:duplicate")}
          title={t("common:duplicate")}
          onClick={() => {
            onClickOpenModalPlaceholder(IngestConfigurePlaceholderContext.Duplicate, params.row);
          }}
        />,
        <GridActionsCellItem
          icon={<EditIcon sx={{ width: 20, height: 20, color: "primary.text" }} />}
          label={t("common:editUpperCase")}
          title={t("common:editUpperCase")}
          onClick={() => {
            onClickOpenModalPlaceholder(IngestConfigurePlaceholderContext.Update, params.row);
          }}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon sx={{ width: 20, height: 20, color: "error.main" }} />}
          label={t("common:delete")}
          title={t("common:delete")}
          onClick={() => {
            ingestService.DeletePlaceholderById(params?.row?.id).then(() => {
              const filter = placeholders.filter((x) => x.id !== params?.row?.id);
              setPlaceholders(filter);
            });
          }}
        />,
      ],
    },
  ];

  useEffect(() => {
    setLoading(true);

    if (params.id)
      ingestService
        .GetPlaceholdersByPackId(parseInt(params.id))
        .then((response) => {
          if (response.pack) {
            setPack(response.pack!.packInfos!);
            setPlaceholders(response.pack!.placeholderRows!);
          }
          setLoading(false);
        })
        .catch(() => {
          navigate(routePaths.ingestPacks.path);
        });
  }, [reload]);

  const additionalActions = (
    <Button
      startIcon={<AddIcon />}
      color="primary"
      variant="contained"
      onClick={() => onClickOpenModalPlaceholder(IngestConfigurePlaceholderContext.Create)}
      disabled={isLoading}
    >
      {t("button:createPlaceholder")}
    </Button>
  );

  return (
    <Stack sx={{ flex: "1 1 0", minHeight: 0 }}>
      <ListPageHeader sx={{ mb: 0 }}>
        <ListPageHeaderLeft
          title={isLoading ? GlobalConst.LOADING_TEXT : pack?.name!}
          subtitle={`${placeholders?.length} ${t("common:placeholders")}`}
          backButton={{ to: routePaths.ingestPacks.path }}
        >
          <Typography variant="body1" color="text.secondary"></Typography>
        </ListPageHeaderLeft>
      </ListPageHeader>
      {isLoading && (
        <CircularProgress
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
          }}
        />
      )}
      {!isLoading && (
        <DataTable
          tableKey={TableUuids.INGEST_PACK_SINGLE_TABLE}
          additionalActions={additionalActions}
          rows={placeholders}
          columns={columnsAction}
          rowHeight={64}
          checkboxSelection={true}
          selectedRows={selectedItemIds}
          setSelectedRows={setSelectedItems}
        />
      )}
    </Stack>
  );
}

export default IngestPacksSingle;
